<script setup lang="ts">
// A few notes:
// - When the activity type is "once" with dates, we just show the dates and don't show a label for "once".
// - If you want to show a label, have a look at the VacancySearchCard for how we split up the "once" frequency depending on the number of dates.
import { Frequency } from '#imports'

const { vacancy } = defineProps<{ vacancy: Vacancy }>()

const { t } = useI18n({ useScope: 'local' })

// TODO: we could move a bit more logic to the composable
const { upcomingDates } = useVacancyDates(vacancy)

const iconClass = 'text-primary-500 size-6 mr-4'
</script>

<template>
  <div class="space-y-2">
    <BaseHeading
      tag="h3"
      size="xl"
      weight="normal"
      class="mb-4"
    >
      {{ t('title') }}
    </BaseHeading>

    <BaseParagraph v-if="vacancy.availabilityDetails">
      <Icon name="ph:info" :class="iconClass" />
      {{ vacancy.availabilityDetails }}
    </BaseParagraph>

    <div v-if="vacancy.timeOfDay && vacancy.timeOfDay.length > 0">
      <Icon name="ph:clock" :class="iconClass" />
      {{ t('timeOfDayLabel') }}:
      {{ vacancy.timeOfDay.map((time) => t(`timeOfDay.${time}`)).join(', ') }}
    </div>

    <div v-if="vacancy.dates && vacancy.dates.length > 0">
      <template v-if="upcomingDates.length > 0">
        <BaseParagraph v-if="vacancy.frequency == Frequency.Regularly">
          <Icon name="ph:calendar-heart" :class="iconClass" />
          {{ t('nextDate', { date: $d(new Date(upcomingDates[0]), 'date') }) }}
        </BaseParagraph>
        <template v-else>
          
          <BaseParagraph>
            <Icon name="ph:calendar-heart" :class="iconClass" />
            {{ t('datesLabel') }}:
          </BaseParagraph>

          <ul class="mt-2 list-disc pl-4">
            <li
              v-for="date in upcomingDates"
              :key="date"
            >
              {{ $d(new Date(date), 'date') }}
            </li>
          </ul>
        </template>
      </template>
      <template v-else>
        <BaseParagraph>
          <Icon name="ph:info" :class="iconClass" />
          {{ t('noUpcomingDatesAvailable') }}
        </BaseParagraph>
      </template>
    </div>
    <BaseParagraph v-else-if="vacancy.frequency">
      <Icon name="ph:calendar-blank" :class="iconClass" />
      {{ t(`frequency.${vacancy.frequency}`) }}
    </BaseParagraph>

    <!-- <BaseParagraph v-else-if="!vacancy.availabilityDetails">
      Onbekend (niet ingevuld)
    </BaseParagraph> -->
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "title": "When and how often is it?",
    "datesLabel": "Takes place on",
    "nextDate": "This activity takes place on {date}",
    "timeOfDayLabel": "Dagdelen",
    "frequency": {
      "once": "This activity takes place on one or more fixed days.",
      "flexible": "This is an activity that takes place occasionally (flexible).",
      "regularly": "This is an activity that takes place regularly."
    },
    "timeOfDay": {
      "morning": "Morning",
      "afternoon": "Afternoon",
      "evening": "Evening"
    },
    "showMore": "Show all dates",
    "showLess": "Show less",
    "noUpcomingDatesAvailable": "No upcoming dates available"
  },
  "nl": {
    "title": "Wanneer en hoe vaak is het?",
    "datesLabel": "Deze activiteit vindt plaats op",
    "nextDate": "De volgende activiteit vindt plaats op {date}",
    "timeOfDayLabel": "Dagdelen",
    "frequency": {
      "once": "Het vindt op één of meerdere vaste dagen plaats",
      "flexible": "Tijdsbesteding is flexibel",
      "regularly": "Het vindt structureel (regelmatig) plaats"
    },
    "timeOfDay": {
      "morning": "ochtend",
      "afternoon": "middag",
      "evening": "avond"
    },
    "showMore": "Toon alle data",
    "showLess": "Toon minder",
    "noUpcomingDatesAvailable": "Geen aankomende data beschikbaar"
  }
}
</i18n>
